import React, { useState, useEffect } from 'react';
import {
  borderColor,
  green,
  placeholder,
  subheadline,
} from '@/shared/styles/colors';
import Dropdown from '@/shared/components/Dropdown';
import { City, State, User } from '@/shared/types/generated';
import { getCityLabel } from '@/components/explore/filters/utils';
import styled from '@emotion/native';
import { BodyText } from '@/shared/components/StyledText';
import { mapEnumToLocationType } from './addressMappers';
import { LocationType } from '@/shared/types/generated';

interface AddressFormProps {
  // Only the address is read, so the admin dashboard can pass a stylist's
  user: Pick<User, 'address'> | null;
  onAddressChange: (address: AddressFormData) => void;
  onValidChange?: (isValid: boolean) => void;
  onFieldBlur?: () => void;
  disabled?: boolean;
}

export interface AddressFormData {
  city: City | '';
  state: State | '';
  locationType: string;
  addressLine1: string;
  addressLine2: string;
  zipCode: string;
  accessDetails: string;
}

const getCityStateOptions = () => {
  const cityStateMap: Record<City, State> = {
    [City.NewYork]: State.Ny,
    [City.LosAngeles]: State.Ca,
    [City.Miami]: State.Fl,
    [City.Boston]: State.Ma,
    [City.Chicago]: State.Il,
    [City.Dallas]: State.Tx,
    [City.Seattle]: State.Wa,
    [City.PalmBeach]: State.Fl,
    [City.LongIsland]: State.Ny,
    [City.SanFrancisco]: State.Ca,
  };

  return Object.values(City).map((city) => ({
    label: `${getCityLabel(city)}, ${cityStateMap[city]}`,
    value: `${city}|${cityStateMap[city]}`,
  }));
};

const getLocationTypeOptions = () => {
  return Object.values(LocationType).map((type) => ({
    label: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase(),
    value: type,
  }));
};

const AddressForm: React.FC<AddressFormProps> = ({
  user,
  onAddressChange,
  onValidChange,
  onFieldBlur,
  disabled = false,
}) => {
  const [cityDropdownVisible, setCityDropdownVisible] = useState(false);
  const [locationTypeDropdownVisible, setLocationTypeDropdownVisible] =
    useState(false);

  const [formData, setFormData] = useState<AddressFormData>(() => {
    const address = user?.address;
    return {
      city: address?.city || '',
      state: address?.state || '',
      locationType: mapEnumToLocationType(address?.locationType),
      addressLine1: address?.streetAddress?.split('\n')[0] || '',
      addressLine2: address?.streetAddress?.split('\n')[1] || '',
      zipCode: address?.zipCode || '',
      accessDetails: address?.accessDetails || '',
    };
  });

  // Update formData when user.address changes (e.g., after refetch)
  useEffect(() => {
    const address = user?.address;
    setFormData({
      city: address?.city || '',
      state: address?.state || '',
      locationType: mapEnumToLocationType(address?.locationType),
      addressLine1: address?.streetAddress?.split('\n')[0] || '',
      addressLine2: address?.streetAddress?.split('\n')[1] || '',
      zipCode: address?.zipCode || '',
      accessDetails: address?.accessDetails || '',
    });
  }, [user?.address]);

  useEffect(() => {
    onAddressChange(formData);
  }, [formData, onAddressChange]);

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidChange) {
      const isValid = Boolean(
        formData.city &&
          formData.state &&
          formData.addressLine1.trim() &&
          formData.zipCode.trim(),
      );
      onValidChange(isValid);
    }
  }, [formData, onValidChange]);

  const handleCityStateSelect = (option: {
    label: string;
    value: string | number;
  }) => {
    const [city, state] = option.value.toString().split('|');
    setFormData((prev) => ({
      ...prev,
      city: city as City,
      state: state as State,
    }));
  };

  const handleLocationTypeSelect = (option: {
    label: string;
    value: string | number;
  }) => {
    setFormData((prev) => ({
      ...prev,
      locationType: option.value.toString(),
    }));
  };

  const getCityStateDisplayValue = () => {
    if (!formData.city || !formData.state) {
      return 'Select city';
    }
    return `${getCityLabel(formData.city)}, ${formData.state}`;
  };

  const getLocationTypeDisplayValue = () => {
    if (!formData.locationType) {
      return 'Select type';
    }
    return (
      formData.locationType.charAt(0).toUpperCase() +
      formData.locationType.slice(1).toLowerCase()
    );
  };

  return (
    <>
      <InputContainer>
        <InputLabel>City & State *</InputLabel>
        <Dropdown
          eyebrowText="City & State"
          options={getCityStateOptions()}
          selectedOption={
            formData.city ? `${formData.city}|${formData.state}` : ''
          }
          selectedOptionLabel={getCityStateDisplayValue()}
          onSelect={handleCityStateSelect}
          isVisible={cityDropdownVisible}
          setIsVisible={setCityDropdownVisible}
          customDropdownButtonStyle={{
            width: '100%',
            opacity: disabled ? 0.6 : 1,
          }}
          customMenuContentStyle={{ width: '100%' }}
          disabled={disabled}
        />
        <HelpText>
          In-person sessions are currently only available in select cities.
        </HelpText>
      </InputContainer>

      <InputContainer>
        <InputLabel>Location Type *</InputLabel>
        <Dropdown
          eyebrowText="Location Type"
          options={getLocationTypeOptions()}
          selectedOption={formData.locationType}
          selectedOptionLabel={getLocationTypeDisplayValue()}
          onSelect={handleLocationTypeSelect}
          isVisible={locationTypeDropdownVisible}
          setIsVisible={setLocationTypeDropdownVisible}
          customDropdownButtonStyle={{
            width: '100%',
            opacity: disabled ? 0.6 : 1,
          }}
          customMenuContentStyle={{ width: '100%' }}
          disabled={disabled}
        />
      </InputContainer>

      <InputContainer>
        <InputLabel>Street Address *</InputLabel>
        <TextInput
          placeholder="Enter street address"
          placeholderTextColor={placeholder}
          value={formData.addressLine1}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, addressLine1: text }))
          }
          onBlur={onFieldBlur}
          editable={!disabled}
          style={disabled ? { opacity: 0.6 } : undefined}
        />
      </InputContainer>

      <InputContainer>
        <InputLabel>Apartment, Suite, etc. (optional)</InputLabel>
        <TextInput
          placeholder="Enter apartment, suite, etc."
          placeholderTextColor={placeholder}
          value={formData.addressLine2}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, addressLine2: text }))
          }
          onBlur={onFieldBlur}
          editable={!disabled}
          style={disabled ? { opacity: 0.6 } : undefined}
        />
      </InputContainer>

      <InputContainer>
        <InputLabel>Zip Code *</InputLabel>
        <TextInput
          placeholder="Enter zip code"
          placeholderTextColor={placeholder}
          value={formData.zipCode}
          inputMode="numeric"
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, zipCode: text }))
          }
          onBlur={onFieldBlur}
          editable={!disabled}
          style={disabled ? { opacity: 0.6 } : undefined}
        />
      </InputContainer>

      <InputContainer>
        <InputLabel>Access Details (optional)</InputLabel>
        <TextArea
          multiline
          placeholder="Enter anything helpful for your stylist on arrival (gate code, parking, etc.)"
          placeholderTextColor={placeholder}
          value={formData.accessDetails}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, accessDetails: text }))
          }
          onBlur={onFieldBlur}
          editable={!disabled}
          style={disabled ? { opacity: 0.6 } : undefined}
        />
      </InputContainer>
    </>
  );
};

// Exported so admin forms beside this one match it
export const InputContainer = styled.View({
  marginBottom: 8,
  width: '100%',
});

export const InputLabel = styled(BodyText)({
  fontSize: 16,
  color: green,
  marginBottom: 12,
  fontWeight: '400',
});

export const TextInput = styled.TextInput({
  backgroundColor: 'white',
  padding: 16,
  borderRadius: 35,
  borderWidth: 1,
  borderColor: borderColor,
  height: 48,
  width: '100%',
  marginBottom: 20,
});

const TextArea = styled.TextInput({
  backgroundColor: 'white',
  padding: 16,
  borderRadius: 9,
  borderWidth: 1,
  borderColor: borderColor,
  height: 100,
  textAlignVertical: 'top',
  width: '100%',
  marginBottom: 20,
});

export const HelpText = styled(BodyText)({
  fontStyle: 'italic',
  fontSize: 13,
  color: subheadline,
  marginTop: -12,
  marginBottom: 16,
});

export default AddressForm;
